import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { StorageService } from '@services';
import { LearnApi, AcademicProfileResume, Certificate, CertificateSection } from '@lighty';
import { environment } from '@env/environment';

@Component({
  selector: 'app-academy-certificates',
  templateUrl: './academy-certificates.component.html',
  styleUrls: ['./academy-certificates.component.scss']
})
export class AcademyCertificatesComponent implements OnInit {

  public modalOverlay: any;
  public modal: any;
  public searchTerm: string = '';

  public company: any;
  public me: any;
  public isLoading: boolean = false;

  // Contadores del hero
  public obtainedCount: number = 0;
  public toCompleteCount: number = 0;

  // Certificados obtenidos (endpoint latest-certificates, leído de course_progress_histories)
  public certificates: any[] = [];
  // Contenidos en progreso (completados que faltan)
  public inProgressCourses: any[] = [];
  // Certificado seleccionado para el modal
  public selectedCertificate: any = null;
  public selectedProvider: string = 'all';
  public isProviderFilterOpen: boolean = false;

  // Confetti del modal "Felicitaciones": 28 partículas con posición/tiempo/forma deterministas.
  private readonly confettiColors = ['#FF2D55', '#22304C', '#FFB020', '#38B2AC', '#FF93A8'];
  public readonly confettiPieces = Array.from({ length: 28 }, (_, i) => {
    const isCircle = i % 4 === 0;
    const width = i % 3 === 0 ? 10 : 7;
    const height = isCircle ? width * 0.7 : (i % 2 === 0 ? width * 0.45 : width);
    return {
      left: (i * 37) % 100,
      delay: (i % 9) * 0.18,
      duration: 2.6 + (i % 5) * 0.35,
      width,
      height,
      radius: isCircle ? 9999 : 2,
      color: this.confettiColors[i % this.confettiColors.length],
    };
  });

  constructor(
    private location: Location,
    private storageService: StorageService,
    private learnApi: LearnApi
  ) {
    this.company = this.storageService.get('company');
    this.me = this.storageService.get('me');
  }

  /** Primer nombre del usuario activo (para el saludo del modal). */
  public get firstName(): string {
    return (this.me?.firstname || '').trim().split(/\s+/)[0] || (this.me?.name || '').trim().split(/\s+/)[0] || '';
  }

  // Si el logo falla al cargar, se muestra el nombre de la empresa
  public logoError: boolean = false;

  /** Logo de la empresa (mismo origen que usa el header) con CDN corregido en dev. */
  public get companyLogo(): string {
    let url: string = this.company?.logo || this.company?.experience?.displayPicture || '';
    if (!url) {
      return '';
    }
    if (!environment.production) {
      url = url.replace(
        'https://api-upgraded.myskillcamp.com.deltabluecdn.com',
        'https://api.myskillcamp.com'
      );
    }
    return url.includes('?') ? url : `${url}?size=512`;
  }

  ngOnInit(): void {
    this.getCertificates();
    this.getAcademicProfile();
  }

  ngAfterViewInit(): void {
    this.modalOverlay = document.getElementById('modal-overlay');
    this.modal = document.getElementById('modal');
  }

  // Orden por fecha de certificación
  public sortOrder: 'recent' | 'oldest' = 'recent';

  public get totalCount(): number {
    return this.obtainedCount + this.toCompleteCount;
  }

  public get availableProviders(): string[] {
    const providers = [...this.certificates, ...this.inProgressCourses]
      .map((item: any) => (item?.providerName || 'Griky').trim())
      .filter((provider: string) => !!provider);

    return Array.from(new Set(providers)).sort((a, b) => a.localeCompare(b));
  }

  public isSortMenuOpen: boolean = false;

  public setSort(order: 'recent' | 'oldest'): void {
    this.sortOrder = order;
    this.isSortMenuOpen = false;
  }

  public toggleSortMenu(): void {
    this.isSortMenuOpen = !this.isSortMenuOpen;
  }

  public toggleProviderFilter(): void {
    this.isProviderFilterOpen = !this.isProviderFilterOpen;
  }

  public setProviderFilter(provider: string): void {
    this.selectedProvider = provider;
    this.isProviderFilterOpen = false;
  }

  /** Normaliza el título del curso/certificado a sentence case (evita mezclar Title Case con sentence case). */
  public toSentenceCase(text: string): string {
    if (!text) {
      return '';
    }
    const lower = text.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  public resetFilters(): void {
    this.searchTerm = '';
    this.selectedProvider = 'all';
  }

  /**
   * Certificados completados filtrados por el buscador y ordenados por fecha.
   */
  public get filteredCertificates(): any[] {
    const removeAccents = (text: string) => {
      return text.normalize('NFD').replace(/[̀-ͯ]/g, '');
    };

    const query = removeAccents((this.searchTerm || '').trim().toLowerCase());
    let list = [...this.certificates];

    if (query) {
      list = list.filter(c => removeAccents((c.contentName || '').toLowerCase()).includes(query));
    }

    if (this.selectedProvider !== 'all') {
      list = list.filter(c => (c.providerName || 'Griky') === this.selectedProvider);
    }

    list = list.sort((a, b) => {
      const timeA = a.certificatedDate?.getTime() || 0;
      const timeB = b.certificatedDate?.getTime() || 0;
      const cmp = timeA - timeB;
      return this.sortOrder === 'recent' ? -cmp : cmp;
    });
    return list;
  }

  /**
   * Certificados obtenidos. Salen de su propio endpoint y no de las listas de contenidos
   * inscritos: esas listas parten del catálogo vivo del alumno y dejaban fuera certificados
   * emitidos sobre contenido que ya no está en sus campus.
   */
  public getCertificates(): void {
    this.learnApi.getLatestCertificates(this.company.id).subscribe({
      next: (section: CertificateSection) => this.buildCertificates(section),
      error: (err) => {
        console.error('Error al cargar los certificados', err);
      }
    });
  }

  /** Perfil académico: solo para los contenidos pendientes ("por completar"). */
  public getAcademicProfile(): void {
    this.isLoading = true;
    this.learnApi.getAcademicProfileResume(this.company.id).subscribe({
      next: (data: AcademicProfileResume) => {
        this.buildInProgressCourses(data);
        this.toCompleteCount = this.countInProgress(data);
      },
      error: (err) => {
        console.error('Error al cargar el resumen académico', err);
      },
      complete: () => {
        this.isLoading = false;
      }
    });
  }

  /**
   * Cuenta los contenidos realmente "en progreso" (progress > 0 && < 100), considerando
   * cursos internos, contenidos externos y rutas de aprendizaje.
   */
  private countInProgress(profile: AcademicProfileResume): number {
    const data = profile?.data;
    if (!data) {
      return 0;
    }
    const all: any[] = [
      // Cursos internos de la plataforma
      ...(data.registeredCourses?.data || []),
      // Contenidos externos (proveedores como netzun, learnlight, etc.)
      ...(data.registeredExternalContents?.data || []),
      // Rutas de aprendizaje (trajects / diplomados)
      ...(data.registeredTrajects?.data || [])
    ];
    return all.filter((item: any) => {
      const p = item?.progress ?? 0;
      const hasCertificate = !!(item?.certificatePath && item.certificatePath.trim() !== '');
      return p > 0 && p < 100 && !hasCertificate;
    }).length;
  }

  /**
   * Lista de certificados obtenidos (cursos, contenidos externos y rutas), ordenada por fecha
   * de certificación desc. El API ya entrega solo filas con certificado.
   */
  private buildCertificates(section: CertificateSection): void {
    this.certificates = (section?.data || [])
      .filter((item: Certificate) => !!item?.certificatePath && item.certificatePath.trim() !== '')
      .map((item: Certificate) => ({
        contentName: item.contentName || '',
        providerName: item.providerName || '',
        certificatePath: item.certificatePath,
        certificatedAt: item.certificatedAt || '',
        // Fecha parseable para el pipe date (evita el formato con espacio no soportado en Safari)
        certificatedDate: item.certificatedAt ? new Date(item.certificatedAt.replace(' ', 'T')) : null,
        image: this.buildImageUrl(item.contentImage, item.contentId),
        providerLogo: this.buildProviderLogo(item)
      }))
      .sort((a: any, b: any) => (b.certificatedAt || '').localeCompare(a.certificatedAt || ''));

    this.obtainedCount = this.certificates.length;
  }

  /**
   * Resuelve la URL de imagen a partir del path relativo (image/xxx.png) o de una
   * URL absoluta. Aplica el reemplazo de CDN en entornos no productivos.
   */
  private buildImageUrl(image: string | null, id: number = 0): string {
    if (!image) {
      return this.fallbackImage(id);
    }

    let url = image.startsWith('http')
      ? image
      : `${environment.envVar.API_URL}/stockage/${image}`;

    if (!environment.production) {
      url = url.replace(
        'https://api-upgraded.myskillcamp.com.deltabluecdn.com',
        'https://api.myskillcamp.com'
      );
    }
    return url.includes('?') ? url : `${url}?size=512`;
  }

  private fallbackImage(id: number = 0): string {
    const normalizedId = (id || 0) % 7;
    return `https://static.myskillcamp.com/lxp/images/logo-empty-state-${normalizedId}.webp`;
  }

  public onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = this.fallbackImage(0);
  }

  /**
   * Logo del proveedor/emisor del certificado (ej. Griky, Netzun, etc.): si el item trae
   * una imagen propia (stockage), se usa esa; si no, cae al ícono estático por slug del
   * proveedor; si tampoco hay, cae al logo de Griky.
   */
  private buildProviderLogo(item: any): string {
    const rawLogo = item?.providerLogo || item?.providerPicture || item?.providerImage || item?.companyLogo;
    if (rawLogo) {
      return this.buildImageUrl(rawLogo);
    }

    const slug = (item?.providerSlug || item?.providerName || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
    if (!slug) {
      return this.fallbackProviderLogo;
    }
    return `https://static.myskillcamp.com/images/integrations/${slug}.svg`;
  }

  private get fallbackProviderLogo(): string {
    return 'https://webservices.griky.co/cdn/img/Griky-Logotipo.webp';
  }

  /** Si el logo del proveedor no carga, cae al logo de marca de Griky. */
  public onProviderLogoError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const fallback = this.fallbackProviderLogo;
    if (img.src !== fallback) {
      img.src = fallback;
    }
  }

  public openModal(certificate: any): void {
    if (!certificate || certificate.isInProgress || !certificate.certificatePath) {
      return;
    }
    this.selectedCertificate = certificate;
    this.modalOverlay.classList.add('flex');
    this.modal.classList.remove('animate-fade-out');
    this.modal.classList.add('animate-fade-in');
  }

  public closeModal(): void {
    this.modal.classList.remove('animate-fade-in');
    this.modal.classList.add('animate-fade-out');
    setTimeout(() => {
      this.modalOverlay.classList.remove('flex');
    }, 250);
  }

  public handleOverlayClick(event: any): void {
    if (event.target === this.modalOverlay) {
      this.closeModal();
    }
  }

  /**
   * Construye la lista de cursos en progreso (completados que faltan).
   * Considera cursos internos, contenidos externos y rutas de aprendizaje con progreso > 0 && < 100.
   */
  private buildInProgressCourses(profile: AcademicProfileResume): void {
    const data = profile?.data;
    if (!data) {
      this.inProgressCourses = [];
      return;
    }

    const sources: any[] = [
      ...(data.registeredCourses?.data || []),
      ...(data.registeredExternalContents?.data || []),
      ...(data.registeredTrajects?.data || [])
    ];

    this.inProgressCourses = sources
      .filter((item: any) => {
        const p = item?.progress ?? 0;
        const hasCertificate = !!(item?.certificatePath && item.certificatePath.trim() !== '');
        // Si ya tiene certificado emitido, se muestra como completado (no como pendiente),
        // aunque el progreso reportado sea menor a 100 (ej. al reiniciar/retomar el curso).
        return p > 0 && p < 100 && !hasCertificate;
      })
      .map((item: any) => ({
        contentName: item.contentName || item.trajectName || '',
        providerName: item.providerName || '',
        certificatePath: null,
        certificatedAt: null,
        certificatedDate: null,
        image: this.buildImageUrl(item.contentImage || item.trajectImage, item.contentId ?? item.trajectId),
        providerLogo: this.buildProviderLogo(item),
        progress: item.progress ?? 0,
        isInProgress: true,
        updatedAt: item.doneAt || '',
        updatedDate: item.doneAt ? new Date(item.doneAt.replace(' ', 'T')) : null
      }))
      .sort((a: any, b: any) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));
  }

  /**
   * Comparte el certificado: usa la API nativa de compartir si está disponible,
   * de lo contrario copia el enlace al portapapeles.
   */
  public shareCertificate(cert: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const url = cert?.certificatePath;
    if (!url) {
      return;
    }
    const nav: any = window.navigator;
    if (nav.share) {
      nav.share({ title: cert.contentName, url }).catch(() => {});
    } else if (nav.clipboard) {
      nav.clipboard.writeText(url).catch(() => {});
    }
  }

  public goBack(): void {
    this.location.back();
  }

  public onSearchChange(): void {
    // Este método se ejecuta cuando el usuario escribe en el buscador
    // Fuerza la actualización de Angular para que se recalcule filteredCertificates
  }

}
